import React, { useEffect, useMemo, useState } from "react";
import "./SolutionDetailsDownloads.css";

import pdfIcon from "../../../../assets/images/allsolutions/downloads/pdf.png";
import downloadIcon from "../../../../assets/images/allsolutions/downloads/download.png";
import { useTranslation } from "react-i18next";
import { getDownloads } from "../../../../api/downloads";
import { resolveMediaUrl } from "../../../../utils/media";

/**
 * @param {{
 *   solution?: Record<string, unknown>,
 *   items?: { id?: number|string, name?: string, title?: string, label?: string, fileUrl?: string, href?: string }[],
 *   hideWhenEmpty?: boolean,
 *   downloadsTag?: string,
 *   downloadsTitle?: string,
 *   downloadsDescription?: string,
 *   downloadAllText?: string,
 * }} props
 */
const SolutionDetailsDownloads = ({
  solution,
  items: itemsProp,
  hideWhenEmpty,
  downloadsTag: downloadsTagProp,
  downloadsTitle: downloadsTitleProp,
  downloadsDescription: downloadsDescriptionProp,
  downloadAllText: downloadAllTextProp,
}) => {
  const useStaticItems = itemsProp !== undefined;

  const staticList = useMemo(() => {
    if (!useStaticItems) return [];
    return (Array.isArray(itemsProp) ? itemsProp : [])
      .map((it, idx) => ({
        id: it?.id ?? idx + 1,
        name: it?.name || it?.title || it?.label || "Document",
        fileUrl: resolveMediaUrl(it?.fileUrl || it?.href),
      }))
      .filter((it) => it.fileUrl);
  }, [itemsProp, useStaticItems]);

  const { t, i18n } = useTranslation();
  const [apiDownloads, setApiDownloads] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const sanitizeFileName = (value) =>
    String(value || "document")
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const toPdfFileName = (title) => {
    const base = sanitizeFileName(title);
    return /\.pdf$/i.test(base) ? base : `${base || "document"}.pdf`;
  };

  const triggerBlobDownload = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    a.rel = "noopener";
    a.click();
    window.URL.revokeObjectURL(blobUrl);
  };

  const downloadItem = async (item) => {
    if (!item?.fileUrl) return false;
    const fileName = toPdfFileName(item.name);
    try {
      const response = await fetch(item.fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch "${item.name}"`);
      }
      const blob = await response.blob();
      triggerBlobDownload(blob, fileName);
      return true;
    } catch {
      window.open(item.fileUrl, "_blank", "noopener,noreferrer");
      return false;
    }
  };

  useEffect(() => {
    if (useStaticItems) return;
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getDownloads({
          language: String(i18n.language || "en").toLowerCase(),
        });
        if (!isMounted) return;
        const normalized = (Array.isArray(data) ? data : [])
          .map((it, idx) => ({
            id: it?.id || idx + 1,
            name: it?.name || it?.title || it?.label || "",
            fileUrl: resolveMediaUrl(it?.fileUrl),
          }))
          .filter((it) => it.name);
        setApiDownloads(normalized);
      } catch {
        if (isMounted) setApiDownloads([]);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language, useStaticItems]);

  const list = useStaticItems ? staticList : apiDownloads;

  const downloadsTag =
    downloadsTagProp ?? solution?.downloadsTag ?? t("sections.solutions.downloads.tag");
  const downloadsTitle =
    downloadsTitleProp ?? solution?.downloadsTitle ?? t("sections.solutions.downloads.title");
  const downloadsDescription =
    downloadsDescriptionProp ??
    solution?.downloadsDescription ??
    t("sections.solutions.downloads.description");
  const downloadAllLabel =
    downloadAllTextProp ??
    solution?.downloadAllText ??
    t("sections.solutions.downloads.downloadAll");
  const emptyLabel = t("sections.solutions.downloads.empty");

  if (hideWhenEmpty && useStaticItems && list.length === 0) {
    return null;
  }

  const handleDownload = async (item) => {
    if (!item?.fileUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadItem(item);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!list.length || isDownloading) return;
    setIsDownloading(true);
    try {
      for (const item of list) {
        if (!item?.fileUrl) continue;
        // eslint-disable-next-line no-await-in-loop
        await downloadItem(item);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 350));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section className="solutionDetailsDownloads">
      <div className="solutionDetailsDownloads-container">
        <div className="solutionDetailsDownloads-tag">{downloadsTag}</div>

        <h2 className="solutionDetailsDownloads-title">{downloadsTitle}</h2>

        <div className="solutionDetailsDownloads-head">
          <p className="solutionDetailsDownloads-desc">{downloadsDescription}</p>

          <button
            type="button"
            className="solutionDetailsDownloads-downloadAllBtn"
            aria-label={downloadAllLabel}
            onClick={handleDownloadAll}
            disabled={!list.length || isDownloading}
          >
            <span>{downloadAllLabel}</span>
            <img src={downloadIcon} alt="" aria-hidden />
          </button>
        </div>

        <div className="solutionDetailsDownloads-list">
          {!list.length ? (
            <div className="solutionDetailsDownloads-item">
              <div className="solutionDetailsDownloads-left">
                <img src={pdfIcon} alt="" aria-hidden />
                <span>{emptyLabel}</span>
              </div>
            </div>
          ) : null}
          {list.map((item) => (
            <div className="solutionDetailsDownloads-item" key={item.id}>
              <div className="solutionDetailsDownloads-left">
                <img src={pdfIcon} alt="" aria-hidden />
                <span>{item.name}</span>
              </div>

              <button
                type="button"
                className="solutionDetailsDownloads-downloadBtn"
                aria-label={`Download ${item.name}`}
                onClick={() => handleDownload(item)}
                disabled={!item?.fileUrl || isDownloading}
              >
                <img src={downloadIcon} alt="" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionDetailsDownloads;
